<script setup lang="ts">
import { computed, defineProps } from "vue";
import { BlogDetail } from "@/types/blogs";
import { useLangStore } from "@/stores/langStore";

const props = defineProps<{
  blog: BlogDetail;
}>();

const langStore = useLangStore();
const langBlocks = computed(
  () =>
    props.blog.blocks[langStore.current] ||
    props.blog.blocks[langStore.fallbackLang]
);
const textBlockStyles = {
  large:
    "sf-pro font-semibold text-xl leading-100p md:text-2xl md:leading-[32px] mb-2 md:mb-4 xl:mb-6",
  medium:
    "sf-pro font-semibold text-base leading-[24px] md:leading-[21px] xl:text-xl xl:leading-[28px] mb-2 xl:mb-4",
  body: "sf-pro font-normal text-sm leading-[21px] md:text-base md:leading-[24px] xl:text-xl xl:leading-[28px] mb-4 md:mb-6 xl:mb-8",
};
</script>

<template>
  <div class="flex flex-col text-left pt-4 px-4 md:px-6 xl:pt-10 xl:px-[72px]">
    <p
      class="text-brown sf-pro font-medium text-2xl leading-100p md:text-[32px] xl:text-[40px] mb-2 xl:mb-4"
    >
      {{
        props.blog.titles[langStore.current] ||
        props.blog.titles[langStore.fallbackLang]
      }}
    </p>
    <p
      class="text-muted sf-pro font-normal text-sm leading-[21px] md:text-base md:leading-[24px] xl:leading-[28px] mb-2 xl:mb-4"
    >
      {{ props.blog.created_at }}
    </p>
    <template v-for="languageBlock in langBlocks" :key="languageBlock.id">
      <img
        v-if="languageBlock.type == 'image'"
        :src="languageBlock.image_url"
        alt="blog image"
        class="rounded-[20px] mb-10"
      />
      <ul
        v-else-if="languageBlock.type == 'list'"
        class="sf-pro font-normal text-sm leading-[21px] md:text-base md:leading-[24px] xl:text-xl xl:leading-[28px] text-brown list-disc list-outside mb-2 md:mb-8 xl:mb-10 pl-2 ml-5 space-y-2"
      >
        <li
          v-for="listItem in languageBlock.list_items"
          :key="listItem.order"
          v-html="listItem.value"
        />
      </ul>
      <div
        v-else-if="languageBlock.type == 'text'"
        class="text-brown [&>*]:text-inherit [&>*]:font-inherit"
        :class="textBlockStyles[languageBlock.style]"
      >
        <div v-html="languageBlock.content.replace(/\r\n/g, '<br>')"></div>
      </div>
    </template>
  </div>
</template>
